import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import toast from 'react-hot-toast';
import calendarGreyIcon from 'assets/images/icons/calendar-grey.svg';
import clockPlusIcon from 'assets/images/icons/clock-plus.svg';
import crossGreyIcon from 'assets/images/icons/cross-grey.svg';
import dataSourceIcon from 'assets/images/icons/data-source.svg';
import formGreyIcon from 'assets/images/icons/form-grey.svg';
import infoCircleIcon from 'assets/images/icons/info-circle.svg';
import userGreyIcon from 'assets/images/icons/user-grey.svg';
import labelIcon from 'assets/images/icons/label.svg';
import locationGreyIcon from 'assets/images/icons/location-grey.svg';
import repeatIcon from 'assets/images/icons/repeat.svg';
import AlertModal from 'components/common-components/AlertModal';
import Badge from 'components/common-components/Badge';
import DuplicateTaskModal from 'pages/tasks/tasks/DuplicateTaskModal';
import ScheduleTasksEditModal from './ScheduleTasksEditModal';
import ScheduleTasksHeader from './ScheduleTasksHeader';
import TableComponent from 'components/table-component-new/Table';
import TablePagination from 'components/table-component-new/TablePagination';
import { scheduleTasksHeaderList } from '../../utils';
import { CallAPI } from 'actions/General';

const ScheduleTasks = ({ scheduleId, taskLabelsOptions, closeModal }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [displayEntries, setDisplayEntries] = useState(10);
  const [scheduleDetail, setScheduleDetail] = useState(null);
  const [taskList, setTaskList] = useState([]);
  const [duplicatedTask, setDuplicatedTask] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [companyFieldOptions, setCompanyFieldOptions] = useState([]);
  const [filter, setFilter] = useState({});
  const [taskDetail, setTaskDetail] = useState(null);
  const [taskEditMode, setTaskEditMode] = useState(null);
  const [companyFieldIdRowIdToDisplayValueMap, setCompanyFieldIdRowIdToDisplayValueMap] =
    useState(null);

  const handleDebouncedFilter = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const clearListing = () => {
    setSelectedRows([]);
    setTaskList([]);
    setTotalRecords(0);
  };

  const getScheduleDetail = async () => {
    const result = await CallAPI('GET_SCHEDULE_DETAIL', null, scheduleId);
    if (result.status) {
      setScheduleDetail(result.data);
    }
  };

  const fetchCompanyFieldList = async () => {
    const { status, data } = await CallAPI('GET_ALL_COMPANY_FIELDS', {});
    if (!status || !data?.length) return;

    const fieldMap = {};
    // unique objects in payload
    const seen = {};
    const payload = { items: [] };

    for (const item of data) {
      fieldMap[item._id] = {
        label: item.fieldTitle,
        dataSourceId: item.dataSource._id,
        dataSourceColumn: item.dataSourceColumn,
      };

      const key = `${item.dataSource._id}_${item.dataSourceColumn}`;
      if (seen[key]) continue;
      seen[key] = true;

      payload.items.push({
        dataSourceId: item.dataSource._id,
        columnId: item.dataSourceColumn,
      });
    }

    const entriesResponse = await CallAPI('GET_MULTIPLE_DATASOURCE_ENTRIES', payload);
    if (entriesResponse.status) {
      const options = [];
      const companyFieldMap = {};
      for (const fieldId in fieldMap) {
        const { dataSourceId, label, dataSourceColumn } = fieldMap[fieldId];
        const ds = entriesResponse.data.find((d) => d.dataSourceId === dataSourceId);
        const entries = (ds?.entries || [])
          .filter((e) => e.entryValue?.keyValue && e.entryValue.columnId === dataSourceColumn)
          .map((e) => {
            const key = `${fieldId}_${e.rowId}`;
            const displayValue = e.entryValue.keyValue;
            companyFieldMap[key] = displayValue;
            return { value: key, displayValue: displayValue };
          });

        options.push({
          value: fieldId,
          displayValue: label,
          selectMultiple: true,
          children: entries,
        });
      }
      setCompanyFieldOptions(options);
      setCompanyFieldIdRowIdToDisplayValueMap(companyFieldMap);
    }
  };

  const fetchScheduleTaskList = async (page = 1, limit = displayEntries) => {
    clearListing();
    const payload = { scheduleId, state: 'active', page, limit };
    if (filter.search) payload.searchText = filter.search;
    if (filter.status?.length) payload.status = filter.status;
    if (filter.priority?.length) payload.priority = filter.priority;
    if (filter.sortBy) payload.sortBy = filter.sortBy;
    if (filter.labels?.length) payload.labels = filter.labels;
    if (filter.companyFieldValues?.length && companyFieldIdRowIdToDisplayValueMap) {
      payload.companyFieldValues = filter.companyFieldValues.map(
        (v) => companyFieldIdRowIdToDisplayValueMap[v],
      );
    }

    // DUE DATE
    if (filter.dueDate?.type && filter.dueDate?.type !== 'all') {
      payload.dueDate = filter.dueDate.type;
      if (filter.dueDate?.type === 'custom') {
        const range = filter.dueDate.range;
        payload.dueFromDate = range[0];
        payload.dueToDate = range[1];
      }
    }

    const result = await CallAPI('GET_TASKS_OF_SCHEDULE', payload, null, setLoading);
    if (result.status) {
      result.data.list.forEach((t) => {
        t.displayRefNumber = `TK-${t.referenceNumber}`;
        t.formTitle = t.form?.title ?? '';
        t.companyFieldName = t.companyField?.fieldTitle ?? '';
      });
      setTaskList(result.data.list);
      setTotalRecords(result.data.count);
    }
  };

  const actionsCallBack = async (action, data) => {
    if (action === 'edit') {
      const result = await CallAPI('GET_TASK_DETAIL', null, data.id);
      if (result.status) {
        setTaskDetail(result.data);
        setTaskEditMode(action);
      }
    } else if (action === 'close' || action === 'delete') {
      const API =
        action === 'close' ? 'CLOSE_BULK_TASKS' : action === 'delete' ? 'DELETE_BULK_TASKS' : null;
      const payload = { taskIds: data.ids };

      const result = await CallAPI(API, payload);
      if (result.status) {
        fetchScheduleTaskList();
      } else{
        toast.error(result.error);
      }
    } else if (action === 'duplicate') {
      const result = await CallAPI('GET_TASK_DETAIL', null, data.id);
      if (result.status) {
        setDuplicatedTask(result.data);
      }
    }
  };

  const openChangeAssigneeCallback = async (task, data) => {
    const payload = { assignedUsers: data.users, assignedGroups: data.groups };

    const result = await CallAPI('UPDATE_TASK_ASSIGNEES', payload, task._id);
    if (result.status) {
      toast.success('Task assignees updated successfully');
      fetchScheduleTaskList();
    }
  };

  const onRowsSelection = (name, value) => {
    let tempSelection = [];
    const mappedObject = (obj) => ({
      id: obj._id,
      status: obj.status,
      scheduleType: scheduleDetail.scheduleType,
      startDate: obj.startDate,
    });

    if (name === 'all') {
      if (value) tempSelection = taskList.map((t) => mappedObject(t));
      else tempSelection = [];
    } else {
      tempSelection = cloneDeep(selectedRows);

      if (value) {
        const selectedTask = taskList.find((t) => t._id === name);

        if (selectedTask) tempSelection.push(mappedObject(selectedTask));
      } else tempSelection = tempSelection.filter((s) => s.id !== name);
    }

    setSelectedRows(tempSelection);
  };

  const handleCloseEditModal = () => {
    setTaskEditMode(null);
    setTaskDetail(null);
  };

  useEffect(() => {
    fetchScheduleTaskList();
  }, [filter]);

  useEffect(() => {
    getScheduleDetail();
    fetchCompanyFieldList();
  }, []);

  return (
    <>
      <AlertModal show={true} size='xl' actionButtons={[]} noPadding>
        <div className='w-100'>
          <div className='d-flex justify-content-between border-bottom-grey-1-h-1 p-3'>
            <span>
              {!scheduleDetail ? '' : new Date(scheduleDetail.createdAt).toLocaleString()}
            </span>
            <img src={crossGreyIcon} className='cursor-pointer' onClick={() => closeModal()} />
          </div>
          {scheduleDetail && (
            <Fragment>
              <div className='border-bottom-grey-1-h-1 p-3'>
                <div className='d-flex gap-2 align-items-center mb-3'>
                  <Badge text={`ST-${scheduleDetail.referenceNumber}`} variant='grey-2' />
                  {scheduleDetail.scheduleType === 'recurring' && <img src={repeatIcon} />}
                  <span className='theme-size-1_2 theme-text-black-1'>{scheduleDetail.title}</span>
                </div>
                <div className='d-flex flex-wrap gap-3 align-items-center'>
                  <img src={infoCircleIcon} />
                  {scheduleDetail.status === 'published' ? (
                    <Badge text='Open' variant='green-2' />
                  ) : scheduleDetail.status === 'draft' ? (
                    <Badge text='Draft' variant='grey-2' />
                  ) : scheduleDetail.status === 'closed' ? (
                    <Badge text='Closed' variant='red-2' />
                  ) : (
                    <></>
                  )}
                  <img src={formGreyIcon} />
                  <Badge text={scheduleDetail.form.title} variant='grey-2' />
                  {scheduleDetail.companyField && (
                    <Fragment>
                      <img src={dataSourceIcon} />
                      <Badge
                        text={scheduleDetail.companyField?.fieldTitle ?? ''}
                        variant='grey-2'
                      />
                    </Fragment>
                  )}
                  {scheduleDetail.startDate && (
                    <>
                      <img src={calendarGreyIcon} />
                      <Badge
                        text={`${new Date(scheduleDetail.startDate).toLocaleString()} -> ${new Date(
                          scheduleDetail.endDate,
                        ).toLocaleString()}`}
                        variant='grey-2'
                      />
                    </>
                  )}
                  {scheduleDetail.recurringDetails && (
                    <Fragment>
                      <img src={clockPlusIcon} />
                      {scheduleDetail.recurringDetails.frequency === 'daily' ? (
                        <Badge text='text_daily' variant='grey-2' />
                      ) : scheduleDetail.recurringDetails.frequency === 'daily' ? (
                        <Badge text='text_weekly' variant='grey-2' />
                      ) : scheduleDetail.recurringDetails.frequency === 'monthly' ? (
                        <Badge text='text_monthly' variant='grey-2' />
                      ) : (
                        <></>
                      )}
                    </Fragment>
                  )}

                  {(scheduleDetail.assignedUsers.length > 0 ||
                    scheduleDetail.assignedUsers.length > 0) && (
                    <Fragment>
                      <img src={userGreyIcon} />
                      <Badge
                        text={`+ ${
                          scheduleDetail.assignedUsers.length + scheduleDetail.assignedUsers.length
                        }`}
                        variant='grey-2'
                      />
                    </Fragment>
                  )}
                  {scheduleDetail.labels.length > 0 && (
                    <div className='d-flex gap-2'>
                      <img src={labelIcon} />
                      {scheduleDetail.labels.map((label, index) => (
                        <div
                          key={index}
                          style={{ backgroundColor: label.color }}
                          className='theme-text-white-1 py-1 px-2 rounded-sm'
                        >
                          {label.name}
                        </div>
                      ))}
                    </div>
                  )}
                  {scheduleDetail.location && (
                    <div className='d-flex gap-2'>
                      <img src={locationGreyIcon} />
                      <span>{scheduleDetail.location}</span>
                    </div>
                  )}
                </div>
              </div>
            </Fragment>
          )}

          <div className='p-3'>
            <ScheduleTasksHeader
              filter={filter}
              setFilter={setFilter}
              selectedRows={selectedRows}
              handleDebouncedFilter={handleDebouncedFilter}
              taskLabelsOptions={taskLabelsOptions}
              companyFieldOptions={companyFieldOptions}
              actionsCallBack={actionsCallBack}
            />

            <TableComponent
              header={scheduleTasksHeaderList}
              data={taskList}
              props={{ translateValueFunc: (val) => t(val), openChangeAssigneeCallback }}
              totalRecords={totalRecords}
              loader={loading}
              isFieldSelectionEnabled={false}
              isPaginationEnabled={false}
              isActionsEnabled={false}
              isSelectionEnabled={true}
              selectionKey={'_id'}
              onSelection={onRowsSelection}
              selectedRows={selectedRows}
              noWrap
            />
            {totalRecords > 0 && (
              <TablePagination
                totalRecords={totalRecords}
                currentPageRecords={taskList.length}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                displayEntries={displayEntries}
                setDisplayEntries={setDisplayEntries}
                listingFunction={fetchScheduleTaskList}
              />
            )}
          </div>
          {duplicatedTask && (
            <DuplicateTaskModal
              duplicatedTask={duplicatedTask}
              setDuplicatedTask={setDuplicatedTask}
              refreshList={fetchScheduleTaskList}
              isNestedModal
            />
          )}
        </div>
      </AlertModal>

      {taskEditMode && (
        <ScheduleTasksEditModal
          handleClose={handleCloseEditModal}
          refreshList={fetchScheduleTaskList}
          data={taskDetail}
        />
      )}
    </>
  );
};

ScheduleTasks.propTypes = {
  scheduleId: PropTypes.string.isRequired,
  taskLabelsOptions: PropTypes.array.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default ScheduleTasks;
